import React, { useState } from 'react';
import { PageSkeleton } from '../DashboardLoader';
import {
  Search,
  Filter,
  MoreVertical,
  Mail,
  Phone,
  MapPin,
  Calendar,
  UserCheck,
  UserX,
  Eye,
  Edit,
  Trash2,
  RefreshCw,
  AlertCircle
} from 'lucide-react';
import { useAdminUsers } from '../../../hooks/useAdminUsers';
import { formatCurrency, formatDate } from '../../../utils/formatPrice';

const AdminUsers = () => {
  const {
    users,
    loading,
    error,
    searchTerm,
    filterStatus,
    currentPage,
    totalPages,
    suspendUserById,
    activateUserById,
    handleSearch,
    handleFilterChange,
    handlePageChange,
    refetch
  } = useAdminUsers();

  // Gestion des actions utilisateur
  const handleSuspendUser = async (userId) => {
    if (!window.confirm('Suspendre ce compte ? L\'utilisateur sera déconnecté et ne pourra plus se connecter.')) return;
    const result = await suspendUserById(userId);
    if (!result.success) {
      alert(`Erreur lors de la suspension: ${result.error}`);
    }
  };

  const handleActivateUser = async (userId) => {
    const result = await activateUserById(userId);
    if (!result.success) {
      alert(`Erreur lors de l'activation: ${result.error}`);
    }
  };

  // Affichage d'erreur
  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
            <h2 className="text-lg font-semibold text-red-800">Erreur de chargement</h2>
          </div>
          <p className="text-red-600 mt-2">{error}</p>
          <button
            onClick={refetch}
            className="mt-3 flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      suspended: 'bg-red-100 text-red-800'
    };
    
    const labels = {
      active: 'Actif',
      inactive: 'Inactif',
      suspended: 'Suspendu'
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  if (loading) return <PageSkeleton />;

  return (
    <div className="p-4">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Gestion des Utilisateurs</h1>
        <p className="text-gray-600">Gérez les comptes utilisateurs de la plateforme</p>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Rechercher un utilisateur..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[rgb(5, 70, 37)]"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <select
              value={filterStatus}
              onChange={(e) => handleFilterChange(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[rgb(5, 70, 37)]"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actif</option>
              <option value="inactive">Inactif</option>
              <option value="suspended">Suspendu</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Utilisateurs ({users.length})
          </h3>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                <th className="px-4 py-3">
                  Utilisateur
                </th>
                <th className="px-4 py-3">
                  Contact
                </th>
                <th className="px-4 py-3">
                  Localisation
                </th>
                <th className="px-4 py-3">
                  Inscription
                </th>
                <th className="px-4 py-3">
                  Statut
                </th>
                <th className="px-4 py-3">
                  Commandes
                </th>
                <th className="px-4 py-3">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-gray-700">
                          {`${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase() || 'U'}
                        </span>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{`${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email}</div>
                        <div className="text-sm text-gray-500">ID: {user.id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{user.email}</div>
                    <div className="text-sm text-gray-500 flex items-center">
                      <Phone className="w-3 h-3 mr-1" />
                      {user.phone}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm text-gray-900 flex items-center">
                      <MapPin className="w-3 h-3 mr-1" />
                      {user.location}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm text-gray-900 flex items-center">
                      <Calendar className="w-3 h-3 mr-1" />
                      {new Date(user.joinDate).toLocaleDateString('fr-FR')}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {getStatusBadge(user.is_active === false ? 'suspended' : 'active')}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{user.orders}</div>
                    <div className="text-sm text-gray-500">{user.totalSpent}€ dépensés</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <button className="p-1.5 border border-gray-200 rounded-md text-gray-500 hover:text-gray-700" title="Voir">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="p-1.5 border border-gray-200 rounded-md text-gray-500 hover:text-blue-600" title="Modifier">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button className="p-1.5 border border-gray-200 rounded-md text-gray-500 hover:text-red-600" title="Supprimer">
                        <Trash2 className="w-4 h-4" />
                      </button>
                      {user.is_active === false ? (
                        <button
                          onClick={() => handleActivateUser(user.id)}
                          className="p-1.5 border border-gray-200 rounded-md text-gray-500 hover:text-green-700"
                          title="Réactiver"
                        >
                          <UserCheck className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleSuspendUser(user.id)}
                          className="p-1.5 border border-gray-200 rounded-md text-gray-500 hover:text-red-600"
                          title="Suspendre"
                        >
                          <UserX className="w-4 h-4" />
                        </button>
                      )}
                      <button className="p-1.5 border border-gray-200 rounded-md text-gray-500 hover:text-gray-700" title="Plus">
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="mt-6 flex items-center justify-between">
        <div className="text-sm text-gray-700">
          Affichage de 1 à {users.length} sur {users.length} résultats
        </div>
        <div className="flex items-center space-x-2">
          <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            Précédent
          </button>
          <button className="px-3 py-2 text-sm font-medium text-white rounded-lg" style={{ backgroundColor: 'rgb(5, 70, 37)' }}>
            1
          </button>
          <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            Suivant
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminUsers;




